/**
 * B87 — the athlete nav config, and the one role branch the desktop rail
 * mounts share.
 *
 * ## Why this file exists
 *
 * Six routes (Today, Races, Team, Settings, Approvals, Guardian home) each
 * carry their own inline copy of the athlete rail's six items. Train
 * (`/plan`, `/plan/week/:n`, `/plan/calendar`) and Runs (`/run`) carried
 * none, because they never mounted the desktop shell at all — that is the
 * B87 defect: on desktop web those two modules dropped the left rail and
 * left the athlete with a back pill (Train) or an ✕ (Runs) as the only way
 * out. Four new mounts landing four more copies of the same list, and four
 * more copies of the coach / guardian / athlete fork, is how a rail drifts
 * apart route by route. The existing copies stay where they are (this lane
 * does not own those files); the NEW mounts share this one.
 *
 * `athleteNavItems` mirrors `coachNav.ts` / `guardian/guardianNav.ts`:
 * same shape, same "pass the active item's id" contract, hrefs as in
 * `today.tsx`'s list.
 */

import { coachNavItems } from './coachNav';
import { guardianNavItems } from '../guardian/guardianNav';
import type { AccountViewId } from '../../lib/accountView';

export interface AthleteNavItem {
  id: string;
  label: string;
  href: string;
  active?: boolean;
}

/** The athlete rail's item ids. `plan` is Train and `runs` is Runs — the
 *  ids five of the six existing route lists already use. */
export type AthleteNavId = 'today' | 'plan' | 'runs' | 'races' | 'team' | 'settings';

/** Build the athlete SideNav item list. Pass the active item's id, or omit
 *  it on a surface that is not one of the six rows. */
export function athleteNavItems(activeId?: AthleteNavId | null): AthleteNavItem[] {
  return [
    { id: 'today', label: 'Today', href: '/today' },
    { id: 'plan', label: 'Train', href: '/plan' },
    { id: 'runs', label: 'Runs', href: '/run' },
    { id: 'races', label: 'Races', href: '/races' },
    { id: 'team', label: 'Team', href: '/team' },
    { id: 'settings', label: 'Settings', href: '/settings' },
  ].map((item) => (activeId && item.id === activeId ? { ...item, active: true } : item));
}

/** The `useRoleGate()` fields this module reads — kept structural so the
 *  helper stays a pure function of two booleans. */
export interface RailRole {
  coachOnly: boolean;
  guardianOnly: boolean;
}

/** An item on whichever rail the fork picked. `badge` exists because the
 *  guardian rail's Approvals row carries a pending count; the athlete and
 *  coach rails never set one. */
export interface RailNavItem extends AthleteNavItem {
  badge?: number;
}

export interface RailNav {
  variant: 'athlete' | 'guardian' | 'coach';
  items: RailNavItem[];
  /** The me-block's second line. `races.tsx` sets it per role. */
  weekLabel: string;
}

/**
 * The three-way rail fork the B87 mounts use, exactly as `races.tsx:560-690`
 * resolves it: a coach-only account gets the reduced coach rail with no row
 * active (Train and Runs have no coach row to be active), a guardian-only
 * account gets the reduced guardian rail with Home active, and everyone else
 * gets the athlete rail with `athleteActiveId` lit.
 *
 * Pure data — the caller builds the `<SideNav>` and owns the me-block, which
 * needs the auth user (`lib/displayName`).
 *
 * ⚖️ PRESENTATION ONLY — this is not a boundary (security review 2026-09-08,
 * finding 2). It consumes `useRoleGate` → `useCoachMode().coachOnly`, the
 * pre-migration composed predicate (`_account_role === 'coach'` and no athlete
 * onboarding); the authoritative fact is `useAccountRole().coachOnly`, which
 * also honours a server-side `role: 'coach'`. A coach-only account provisioned
 * server-side without the CG-7 context flag would be handed the athlete rail
 * here. What keeps that unreachable is `AthleteSurfaceGuard` (`App.tsx`), which
 * wraps all four B87 routes, reads the authoritative signal and renders the
 * boundary card before any of this runs. Change that wrapping and this fork
 * stops being decorative.
 */
export function railNavFor(role: RailRole, athleteActiveId: AthleteNavId): RailNav {
  if (role.coachOnly) {
    return { variant: 'coach', items: coachNavItems(null), weekLabel: 'Coach' };
  }
  if (role.guardianOnly) {
    return {
      variant: 'guardian',
      items: guardianNavItems('guardian-home'),
      weekLabel: 'Guardian',
    };
  }
  return { variant: 'athlete', items: athleteNavItems(athleteActiveId), weekLabel: '' };
}

/**
 * W2-CHROME (CH-4, CH-5) — the rail for a chosen VIEW, with `settings_2_0_v1`
 * on. Where `railNavFor` forks on two role booleans (and so picks the guardian
 * rail first for an account that is both coach-only and guardian-only, B392),
 * this is a pure function of the view the account chose, so the desktop rail
 * and the tab bar below 1024 (`lib/nav/tabSets.ts`) show the same set.
 *
 * The active row comes from the pathname, because the route that mounts the
 * rail may have forked on a different role than the view. The same three rails
 * as `railNavFor`: athlete (Today, Train, Runs, Races, Team, Settings), coach
 * (Team, Meets, Settings) and guardian (Home, Approvals, Settings).
 */
export function railNavForView(view: AccountViewId, pathname: string): RailNav {
  const at = (prefix: string): boolean =>
    pathname === prefix || pathname.startsWith(`${prefix}/`);
  if (view === 'coaching') {
    const active = at('/team') ? 'team' : at('/races') ? 'races' : at('/settings') ? 'settings' : null;
    return { variant: 'coach', items: coachNavItems(active), weekLabel: 'Coach' };
  }
  if (view === 'guardian') {
    const active = at('/approvals') ? 'approvals' : at('/settings') ? 'settings' : 'guardian-home';
    return { variant: 'guardian', items: guardianNavItems(active), weekLabel: 'Guardian' };
  }
  const active: AthleteNavId | null =
    pathname === '/' || at('/today')
      ? 'today'
      : at('/plan')
        ? 'plan'
        : at('/run')
          ? 'runs'
          : at('/races')
            ? 'races'
            : at('/team')
              ? 'team'
              : at('/settings')
                ? 'settings'
                : null;
  return { variant: 'athlete', items: athleteNavItems(active), weekLabel: '' };
}
